// skinLoader.ts
import type { SkinModule, SkinContext, ApiSnapshot, HeaderInfo, Settings, ISO8601 } from "./types";
import { DISPLAY_TZ, DISPLAY_LOCALE, UI_BGCOLOR } from "./config";
import { selectSessionsForBoard, type SelectionOptions as SessionSelectionOptions, } from "./utils/sessions";
import { renderMessage } from "@ui/messages";
//import { renderMessage } from "./ui/messages";

type SkinElement = HTMLElement & { context: SkinContext };

const loaders = import.meta.glob<{ default: SkinModule }>("./skins/**/index.ts");
const toPath = (k: string) => `./skins/${k}/index.ts`;

let activeSkin: SkinModule | null = null;
let activeSkinKey: string | null = null;
let activeRoot: SkinElement | null = null;

function coerceLocale(input?: string): string | undefined {
  if (!input) return undefined;
  try {
    // canonicalizes & validates BCP‑47 tags
    return Intl.getCanonicalLocales(input)[0];
  } catch {
    return undefined;
  }
}

function coerceTimeZone(input?: string): string | undefined {
  if (!input) return undefined;
  try {
    // throws if invalid tz; resolvedOptions() returns canonical form
    return new Intl.DateTimeFormat("en", { timeZone: input }).resolvedOptions().timeZone;
  } catch {
    return undefined;
  }
}

export function pickLocaleAndTz(s: { locale?: string; timeZone?: string }) {
  const url = new URL(location.href);
  const qLocale = coerceLocale(url.searchParams.get("locale") ?? undefined);
  const qTz     = coerceTimeZone(
    url.searchParams.get("tz") ?? url.searchParams.get("timezone") ?? undefined
  );

  // precedence: query → settings → env → browser → hard default
  const locale =
    qLocale ??
    coerceLocale(s.locale) ??
    coerceLocale(DISPLAY_LOCALE) ??
    coerceLocale(navigator.language) ??
    "sv-SE";

  const timeZone =
    qTz ??
    coerceTimeZone(s.timeZone) ??
    coerceTimeZone(DISPLAY_TZ) ??
    coerceTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone) ??
    "Europe/Stockholm";

  return { locale, timeZone };
}

// --- helpers (epoch seconds aware) ---
function fromEpochSeconds(sec?: number): Date | null {
  if (typeof sec !== "number" || !isFinite(sec)) return null;
  // API gives seconds → convert to ms
  const d = new Date(sec * 1000);
  return isNaN(d.getTime()) ? null : d;
}

function dayKeyAndLabelEpochSec(
  sec: number | undefined,
  locale: string,
  timeZone: string,
): { key: string; label: string } {
  const d = fromEpochSeconds(sec);
  if (!d) return { key: "unscheduled", label: "" };

  // cache formatters per call (fast + no hardcoded patterns)
  const partsFmt = new Intl.DateTimeFormat(locale, {
    timeZone,
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  });
  const labelFmt = new Intl.DateTimeFormat(locale, {
    timeZone,
    dateStyle: "medium", // locale-aware label
  });

  const parts = partsFmt.formatToParts(d);
  const y  = parts.find(p => p.type === "year")?.value;
  const m  = parts.find(p => p.type === "month")?.value;
  const da = parts.find(p => p.type === "day")?.value;
  if (!y || !m || !da) return { key: "unscheduled", label: "" };

  const key = `${y}-${m}-${da}`;                // stable internal key
  const anchor = new Date(Date.UTC(+y, +m - 1, +da)); // label consistent in tz
  const label = labelFmt.format(anchor);

  return { key, label };
}

// function computeVM(
//     agenda: SkinContext["agenda"],
//     locale: string,
//     timeZone: string,
//     requestTime:number | undefined,
//     requestTimeISO:ISO8601 | undefined
//   ) {
//   const sessionsByDay: Record<string, any[]> = {};
//   const dayLabels: Record<string, string> = {};

//   for (const s of agenda.sessions ?? []) {
//     // Your API field (epoch seconds). Fallbacks keep it resilient if schema ever varies.
//     const startSec: number | undefined =
//       (s as any).time_start ??
//       (typeof (s as any).start === "number" ? (s as any).start : undefined);

//     const { key, label } = dayKeyAndLabelEpochSec(startSec, locale, timeZone);
//     (sessionsByDay[key] ||= []).push(s);
//     if (!dayLabels[key]) dayLabels[key] = label;
//   }

//   return {
//     sessionsByDay,
//     dayLabels,
//     nowISO: new Date().toISOString(),
//     requestTime,
//     requestTimeISO
//   };
// }

function computeVM(
  agenda: SkinContext["agenda"],
  locale: string,
  timeZone: string,
  requestTime: number | undefined,
  requestTimeISO: ISO8601 | undefined
) {
  const sessionsByDay: Record<string, any[]> = {};
  const dayLabels: Record<string, string> = {};

  for (const s of agenda.sessions ?? []) {
    // Robustly read epoch seconds
    const startSec: number | undefined =
      (s as any).time_start ??
      (typeof (s as any).start === "number" ? (s as any).start : undefined);

    if (typeof startSec !== "number") continue;

    const { key, label } = dayKeyAndLabelEpochSec(startSec, locale, timeZone);
    (sessionsByDay[key] ||= []).push(s);
    if (!dayLabels[key]) dayLabels[key] = label;
  }

  // Sort sessions within each day by start time (then end as tiebreaker)
  for (const key of Object.keys(sessionsByDay)) {
    sessionsByDay[key].sort((a: any, b: any) => {
      const as = (a.time_start ?? a.start ?? 0) | 0;
      const bs = (b.time_start ?? b.start ?? 0) | 0;
      if (as !== bs) return as - bs;
      const ae = (a.time_end ?? a.end ?? 0) | 0;
      const be = (b.time_end ?? b.end ?? 0) | 0;
      return ae - be;
    });
  }

  return {
    sessionsByDay,
    dayLabels,
    nowISO: new Date().toISOString(),
    requestTime,
    requestTimeISO,
  };
}

// function setBackground(settings: SkinContext["settings"]) {
//   const bg = settings.background;
//   if (!bg?.url) return;
//   const url = bg.version ? `${bg.url}?v=${encodeURIComponent(bg.version)}` : bg.url;
//   const want = `url("${url}")`;
//   if (document.body.style.backgroundImage !== want) {
//     document.body.style.backgroundImage = want;
//     document.body.style.backgroundSize = "cover";
//     document.body.style.backgroundPosition = "center";
//     document.body.style.backgroundAttachment = "fixed";
//   }
// }



// function setBackground(settings?: SkinContext["settings"]) {

//   console.log('SETTINGS', settings)

//   const s = document.body.style;
//   const bg = settings?.background;

//   // Accept string URL or object { url?: string; color?: string }
//   let url: string | undefined;
//   let color: string | undefined;

//   if (typeof bg === "string") {
//     url = bg;
//   } else if (bg && typeof bg === "object") {
//     if (typeof bg.url === "string" && bg.url) url = bg.url;    // presigned: do NOT modify
//     if (typeof (bg as any).color === "string" && (bg as any).color) {
//       color = (bg as any).color;
//     }
//   }

//   if (url || color) {
//     if (url) s.setProperty("--mm-bg-image", `url("${url}")`);
//     else s.removeProperty("--mm-bg-image");

//     s.setProperty("--mm-bg-color", color ?? UI_BGCOLOR);
//     document.body.classList.add("mm-board-bg");
//   }
//   else {
//     s.removeProperty("--mm-bg-image");
//     s.removeProperty("--mm-bg-color");
//     document.body.classList.remove("mm-board-bg");
//   }
// }


export function setBackground(settings?: SkinContext["settings"]) {
  const s = document.body.style;
  const bg = settings?.background as { url?: string; timeStamp?: number } | undefined;

  const url = bg?.url;
  const stamp = bg?.timeStamp; // number | undefined

  // clear when no bg
  if (!url) {
    s.removeProperty("--mm-bg-image");
    s.removeProperty("--mm-bg-color");
    document.body.classList.remove("mm-board-bg");
    delete document.body.dataset.bgStamp;
    delete document.body.dataset.bgId;

    renderMessage({ type: "SUCCESS", message: "Background file removed." });

    return;
  }

  // compare against previously applied content
  const prevStamp = document.body.dataset.bgStamp ?? "";
  const nextStamp = stamp != null ? String(stamp) : "";

  // OPTIONAL fallback if your API ever omits timeStamp:
  const stableId = new URL(url, location.origin).pathname; // ignore presigned query
  const prevId = document.body.dataset.bgId ?? stableId;

  const changed =
    (nextStamp && nextStamp !== prevStamp) || // prefer timestamp when present
    (!nextStamp && stableId !== prevId);      // else fallback to pathname

  if (!changed) return; // same content → do nothing (no download, no flash)

  // preload to avoid flash, then swap
  const img = new Image();
  img.onload = () => {
    s.setProperty("--mm-bg-color", UI_BGCOLOR);
    s.setProperty("--mm-bg-image", `url("${url}")`);
    document.body.classList.add("mm-board-bg");

    // remember what we applied
    document.body.dataset.bgStamp = nextStamp;
    document.body.dataset.bgId = stableId;

    renderMessage({ type: "SUCCESS", message: "New background file loaded." });
  };
  img.onerror = () => {
    // keep previous background if load fails
    console.warn("Failed to load background:", url);
  };
  img.src = url;
}



export function resetBoard(container: HTMLElement): void {

  console.log('Resetting board')

  try {
    // let the skin clean up its own resources
    if (activeSkin?.unmount && activeRoot) {
      activeSkin.unmount(activeRoot);
    }
  } catch (err) {
    console.warn("Skin unmount error:", err);
  } finally {
    // remove the root from the container if still attached
    if (activeRoot && activeRoot.parentElement === container) {
      container.removeChild(activeRoot);
    }
    activeRoot = null;
    activeSkin = null;
    activeSkinKey = null;

    // clear any board-wide background/styles
    setBackground(undefined);

    // optional: blank the host container
    container.textContent = "";


    const s = document.body.style;
  s.removeProperty("--mm-bg-image");
  s.removeProperty("--mm-bg-color");
  s.background = "";        // <- important
  s.backgroundImage = "";   // <- important
  document.body.classList.remove("mm-board-bg");

  }
}

// <-- simplified: only top-level fields
function deriveHeader(snap: ApiSnapshot): HeaderInfo {
  return {
    agendaTitle: snap.agendaTitle,
    label: snap.label,
    timestamp: snap.timestamp,
  };
}

export async function applySnapshot(
  snapshot: ApiSnapshot,
  container: HTMLElement,
  fallbackSkin = "default",
  sessionOpts?: SessionSelectionOptions // { baseDate?, showUnfinishedOnlyToday? }
) {
  setBackground(snapshot.settings);

  const rawSessions = snapshot.agenda?.sessions ?? [];
  const selection = selectSessionsForBoard(rawSessions, sessionOpts);
  const filteredSessions = selection.kind === "NONE" ? [] : selection.sessions;

  const filteredAgenda = {
    ...snapshot.agenda,
    sessions: filteredSessions,
  };

  const sessionsMeta = selection.kind === "NONE"
    ? { day: "NONE", message: "No more sessions today" }
    : { day: selection.kind, message: undefined };



  // const nextSkinKey = snapshot.settings.skin || fallbackSkin;
  // const header = deriveHeader(snapshot);
  // const { locale, timeZone } = pickLocaleAndTz(snapshot.settings);

  const nextSkinKey = snapshot.settings.skin || fallbackSkin;
  const header = deriveHeader({ ...snapshot, agenda: filteredAgenda }); // ensure header uses filtered data
  const { locale, timeZone } = pickLocaleAndTz(snapshot.settings);


  const ctx: SkinContext = {
    container,
    settings: snapshot.settings,
    agenda: filteredAgenda,
    requestTime: snapshot.requestTime,
    header,
    config: { 
        locale,
        timeZone,
        skin: nextSkinKey
    },
    vm: computeVM(
      filteredAgenda,
      locale,
      timeZone,
      snapshot.requestTime,
      snapshot.requestTimeISO
    ),
    // Optional: expose meta for skins that want to display the fallback text
    ui: { 
      ...(snapshot as any).ui,
      meta: sessionsMeta
    } as any,
  };

  if (activeSkin && nextSkinKey === activeSkinKey && typeof activeSkin.update === "function") {
    activeSkin.update(activeRoot!, ctx);
    return;
  }


  const loader = loaders[toPath(nextSkinKey)] || loaders[toPath(fallbackSkin)];
  if (!loader) throw new Error(`Skin not found: ${nextSkinKey}`);

  const mod = (await loader()).default;
  mod.define();

  if (activeSkin?.unmount && activeRoot) activeSkin.unmount(activeRoot);
  if (activeRoot && activeRoot.parentElement === container) container.removeChild(activeRoot);

  activeRoot = mod.mount(ctx);
  activeSkin = mod;
  activeSkinKey = nextSkinKey;
}
